import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, ExternalLink, MapPin, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import AutocompleteInput from "@/components/AutocompleteInput";

/**
 * Reusable directory page for the small "roster" tabs
 * (Announcer, Photographer, Videographer).
 *
 * Backend: /api/directory/{type} — GET/POST/PUT/DELETE
 */
export default function DirectoryPage({ type, title, subtitle, singularNoun }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setError("");
    api.get(`/directory/${type}`)
      .then((r) => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };
  useEffect(load, [type]);

  const remove = async (entry) => {
    if (!window.confirm(`Remove "${entry.name}"?`)) return;
    try {
      await api.delete(`/directory/${type}/${entry.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-tiny mb-2">{title}</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] mb-2">{title}</h1>
          {subtitle && <p className="text-stone-400 text-sm">{subtitle}</p>}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary inline-flex items-center gap-1.5"
          data-testid={`directory-${type}-add`}
        >
          <Plus size={14} /> Add {singularNoun}
        </button>
      </div>

      {error && <div className="text-rose-400 text-sm mb-4">Failed to load: {error}</div>}
      {entries === null && !error && (
        <div className="flex items-center gap-2 text-stone-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}
      {entries && entries.length === 0 && (
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500">
          No {type}s yet. Click <span className="text-orange-400">Add {singularNoun}</span> above to get started.
        </div>
      )}
      {entries && entries.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`directory-${type}-grid`}>
          {entries.map((e) => (
            <div
              key={e.id}
              className="border border-stone-800 hover:border-orange-700 transition-colors p-4"
              data-testid={`directory-${type}-card-${e.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-display uppercase text-lg font-bold leading-tight truncate">{e.name}</div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditing(e)}
                    className="p-1 text-stone-500 hover:text-orange-400"
                    title="Edit"
                    data-testid={`directory-${type}-edit-${e.id}`}
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => remove(e)}
                    className="p-1 text-stone-500 hover:text-rose-400"
                    title="Delete"
                    data-testid={`directory-${type}-delete-${e.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              {e.description && (
                <p className="text-xs text-stone-400 mb-2 line-clamp-3">{e.description}</p>
              )}
              <div className="space-y-1.5">
                {e.location && (
                  <div className="text-[11px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1">
                    <MapPin size={10} /> {e.location}
                  </div>
                )}
                {e.url && (
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-orange-400 hover:text-orange-300 inline-flex items-center gap-1 truncate max-w-full"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <ExternalLink size={10} /> {e.url}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {(showAdd || editing) && (
        <DirectoryFormModal
          type={type}
          singularNoun={singularNoun}
          entry={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DirectoryFormModal({ type, singularNoun, entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const [name, setName] = useState(entry?.name || "");
  const [description, setDescription] = useState(entry?.description || "");
  const [url, setUrl] = useState(entry?.url || "");
  const [location, setLocation] = useState(entry?.location || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), description, url, location };
      if (isEdit) {
        await api.put(`/directory/${type}/${entry.id}`, body);
        toast.success("Updated");
      } else {
        await api.post(`/directory/${type}`, body);
        toast.success(`${singularNoun} added`);
      }
      onSaved();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`directory-${type}-modal`}
    >
      <div className="bg-stone-950 border border-orange-700 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-tiny mb-1">{isEdit ? "Edit" : "New"}</div>
            <h2 className="font-display uppercase text-2xl font-bold leading-tight">{isEdit ? entry.name : `Add ${singularNoun}`}</h2>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-200" data-testid={`directory-${type}-modal-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3 mb-5">
          <div>
            <div className="label-tiny mb-1">Name *</div>
            <AutocompleteInput
              autoFocus
              field={type}
              value={name}
              onChange={setName}
              placeholder={type === "announcer" ? "e.g. Jonathon Cole Show" : "e.g. Alex Baker Media"}
              className="field-input w-full"
              data-testid={`directory-${type}-form-name`}
            />
          </div>
          <div>
            <div className="label-tiny mb-1">Description</div>
            <AutocompleteInput
              as="textarea"
              field="description"
              rows={3}
              value={description}
              onChange={setDescription}
              placeholder="optional short bio"
              className="field-input w-full"
              data-testid={`directory-${type}-form-description`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-tiny mb-1">Location</div>
              <AutocompleteInput
                field="location"
                value={location}
                onChange={setLocation}
                placeholder="e.g. Olds, AB"
                className="field-input w-full"
                data-testid={`directory-${type}-form-location`}
              />
            </div>
            <div>
              <div className="label-tiny mb-1">Website / URL</div>
              <AutocompleteInput
                field="url"
                value={url}
                onChange={setUrl}
                placeholder="https://…"
                className="field-input w-full"
                data-testid={`directory-${type}-form-url`}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary" data-testid={`directory-${type}-form-cancel`}>Cancel</button>
          <button
            onClick={save}
            disabled={!name.trim() || saving}
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`directory-${type}-form-save`}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {isEdit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
